import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Base64 } from "js-base64";

import toast from "components/partials/toast/toast";

import { Unpersisted } from "types/utility";

import type { TouchpointAttributes } from "hooks/api/use-touchpoints";
import { useCreateParentEmail } from "hooks/api/use-touchpoint-versions";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { mapTouchpointVersionDTO } from "utilities/api-mappers";

import { useParentTouchpointContext } from "components/modals/create-parent-touchpoint-modal/context/parent-touchpoint-context";

import { TouchpointType, TouchpointRedirectDestination } from "types/touchpoint";
import {
  DEFAULT_TOUCHPOINT_VAL,
  PARENT_VALUE,
} from "components/modals/create-parent-touchpoint-modal/create-parent-touchpoint-modal.constants";
import type { CreateEmailTouchpointWithContentRequestPopulationType } from "generated/schemas";

import { formatTouchpointClassesForApi } from "components/modals/creative-modal/creative-modal-utils";

const touchpointVersionApi = getTouchpointVersion();

export interface TouchpointPreviewData {
  finalHtml?: string;
  beeJson?: string;
}

interface ParentEmailAttributes extends TouchpointAttributes {
  fiscalYear?: number;
}

export const useCreateParentTouchpointPreview = () => {
  const {
    selectedVersion,
    selectedCampaign,
    generatedEmails,
    currentEmailIndex,
    currentClient,
    currentTouchpoint,
    defaultCampaign,
  } = useParentTouchpointContext();

  const { getValues } = useFormContext();
  const navigate = useNavigate();
  const parentEmailScope = {
    clientId: currentClient.id,
    campaignId: selectedCampaign.id,
    touchpointId: currentTouchpoint.id,
  };
  const createParentEmailMutation = useCreateParentEmail(parentEmailScope);

  const fetchStudentEmail = async (): Promise<TouchpointPreviewData> => {
    if (!selectedVersion?.id) {
      return {};
    }

    try {
      const dto = await touchpointVersionApi.getOneVersion({
        clientId: currentClient.id,
        campaignId: defaultCampaign.id,
        touchpointId: currentTouchpoint.id,
        id: selectedVersion.id,
      });
      const touchpointData = mapTouchpointVersionDTO(dto);

      return {
        finalHtml: touchpointData.finalHtml,
        beeJson: touchpointData.data?.jsonOutput,
      };
    } catch (error) {
      console.error("Failed to fetch touchpoint data:", error);
      return {};
    }
  };

  // hanlder for creating both touchpoint and version for selected email
  const createEmail = (
    attributes: Unpersisted<ParentEmailAttributes>,
    generatedContent?: { html: string; beeJson: string },
    actionType: TouchpointRedirectDestination = "toTouchpoint",
  ) => {
    if (selectedVersion) {
      const formData = getValues();

      // Base64 encode HTML and JSON content
      const htmlOutput = generatedContent?.html || selectedVersion?.finalHtml || "";
      const jsonOutput =
        generatedContent?.beeJson ||
        selectedVersion?.data?.jsonOutput ||
        selectedVersion?.data ||
        "";

      const dataContent = Base64.encode(
        JSON.stringify({
          htmlOutput: htmlOutput,
          jsonOutput: jsonOutput,
        }),
      );

      const clientIdOverride = selectedCampaign.clientId?.toString() ?? "";
      createParentEmailMutation
        .mutateAsync({
          versionId: selectedVersion.id,
          data: {
            // Touchpoint fields
            name: attributes.name,
            contentType: attributes.contentType,
            ...(attributes.folderId ? { folderId: attributes.folderId } : {}),
            copyCode: attributes.copyCode,
            populationType: PARENT_VALUE as CreateEmailTouchpointWithContentRequestPopulationType,
            classes: formatTouchpointClassesForApi(formData.classes ?? currentTouchpoint.classes),
            insightsActivator: attributes.insightsActivator,
            // Version fields
            applyClientTheme: formData.applyClientTheme,
            data: dataContent,
            fiscalYear: attributes.fiscalYear,
          } as unknown as Parameters<typeof createParentEmailMutation.mutateAsync>[0]["data"],
        })
        .then((results) => {
          const navigationUrl =
            actionType === "toBuilder"
              ? `/clients/${selectedCampaign.clientId}/campaigns/${formData.campaignId}/creative/${results.touchpointId}/versions/${results.id}/email-builder`
              : `/clients/${selectedCampaign.clientId}/campaigns/${formData.campaignId}/creative/${results.touchpointId}`;

          if (actionType === "toBuilder") {
            // First claim the lock before navigating
            return touchpointVersionApi
              .claimLock({
                clientId: clientIdOverride,
                campaignId: formData.campaignId,
                touchpointId: results.touchpointId,
                id: results.id,
              })
              .then(() => {
                toast.success({
                  data: {
                    title: "Created email",
                  },
                });
                navigate(navigationUrl);
              })
              .catch((error) => {
                console.error("Error claiming lock:", {
                  error,
                  versionId: results.id,
                  touchpointId: results.touchpointId,
                });
                throw error;
              });
          } else {
            toast.success({
              data: {
                title: "Created email",
              },
            });
            navigate(navigationUrl);
          }
        })
        .catch((err) => {
          console.error("Error creating email:", {
            error: err,
            response: err?.response?.data,
            status: err?.response?.status,
            versionId: err?.response?.data?.versionId,
            touchpointId: err?.response?.data?.touchpointId,
          });
          toast.error({
            data: {
              title: "Failed to create email",
            },
          });
        });
    } else {
      console.error("No selected version found");
      toast.error({
        data: {
          title: "Failed to create email",
          message: "No selected version found",
        },
      });
    }
  };

  const handleCreateEmail = (actionType: TouchpointRedirectDestination) => {
    const data = getValues();

    const emailAttributes: Unpersisted<ParentEmailAttributes> = {
      name: data.touchpointName,
      campaignId: data.campaignId,
      contentType: data.contentType,
      ...(data.folderId ? { folderId: data.folderId } : {}),
      type: TouchpointType.EMAIL,
      clientId: currentClient.id,
      lastModifiedDate: DEFAULT_TOUCHPOINT_VAL,
      copyCode: data.copyCode,
      fiscalYear: data.fiscalYear,
      populationType: PARENT_VALUE,
      insightsActivator: data.insightsActivator,
    };

    // Use generated content if available
    const generatedContent =
      generatedEmails.length > 0 ? generatedEmails[currentEmailIndex] : undefined;
    createEmail(emailAttributes, generatedContent, actionType);
  };

  return {
    handleCreateEmail,
    fetchStudentEmail,
  };
};
